import { auth } from '../firebase/config/config';
import { getApiBaseUrl } from '../config/apiConfig';

/**
 * Makes an authenticated API call with Firebase token
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @param {Object} user - Firebase user object (optional, will use auth.currentUser if not provided)
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedFetch = async (url, options = {}, user = null) => {
  const currentUser = user || auth.currentUser;

  // Get the Firebase token for authentication
  const token = currentUser ? await currentUser.getIdToken() : null;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add Authorization header if token is available
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Prepend Base URL
  const baseUrl = getApiBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  return fetch(fullUrl, {
    ...options,
    credentials: options.credentials || 'include',
    headers,
  });
};

/**
 * Makes an authenticated POST request
 * @param {string} url - The API endpoint URL
 * @param {Object} data - The data to send in the request body
 * @param {Object} user - Firebase user object (optional)
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedPost = async (url, data, user = null) => {
  return authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  }, user);
};

/**
 * Makes an authenticated GET request
 * @param {string} url - The API endpoint URL
 * @param {Object} user - Firebase user object (optional)
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedGet = async (url, user = null) => {
  return authenticatedFetch(url, {
    method: 'GET',
  }, user);
};
